import { useMutation } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import type { AxiosError, AxiosResponse } from "axios"

import { SidAPI } from "../../../services/SidAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { CustomTokenRefresh } from "../../../types/generated/authentication/CustomTokenRefresh"
import { setAccessToken, setIsUserAuthenticated, setShouldAuthenticate } from "../Slice"

interface LocationProps {
  state?: {
    from?: {
      pathname: string
    }
  }
}

interface UseAutoLoginProps {
  redirectPath: string
}

export function useAutoLogin({ redirectPath }: UseAutoLoginProps): {
  isRefreshing: boolean
  refreshError: AxiosError | null
} {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation() as LocationProps
  const shouldAuthenticate = useSelector((state: RootState) => state.auth.shouldAuthenticate)
  const isUserAuthenticated = useSelector((state: RootState) => state.auth.isUserAuthenticated)

  // Determine where to redirect after successful login - either the intercepted route or default path
  const redirectToPath = location?.state?.from?.pathname || redirectPath || "/projects?page=model"

  const refreshTokenMutation = useMutation<AxiosResponse<CustomTokenRefresh>, AxiosError, string | undefined>(
    SidAPI.refreshToken,
    { mutationKey: ["refresh"] },
  )

  shouldAuthenticate &&
    !isUserAuthenticated &&
    !refreshTokenMutation.isLoading &&
    refreshTokenMutation
      .mutateAsync(undefined)
      .then((res) => {
        if (res.status === 200) {
          dispatch(setAccessToken(res.data.access))
          dispatch(setIsUserAuthenticated(true))
          dispatch(setShouldAuthenticate(false))
          navigate(redirectToPath, { replace: true })
        }
      })
      .catch(() => {
        dispatch(setIsUserAuthenticated(false))
        dispatch(setShouldAuthenticate(false))
      })

  return {
    isRefreshing: refreshTokenMutation.isLoading,
    refreshError: refreshTokenMutation.error,
  }
}
